<script setup lang="ts">
import { transformSlug } from "@/utils/transformSlug";
import { defineProps, defineEmits } from "vue";
import { useTranslate } from "@/composables/useTranslate";

type Props = {
  step: any;
};

const props = defineProps<Props>();

type Emits = {
  (
    event: "setSelectedInput",
    input: any,
    inputIndex?: number,
    groupInputIndex?: number
  ): void;
};

defineEmits<Emits>();
const { t } = useTranslate();
</script>

<template>
  <div
    class="w-full p-3 bg-white rounded-primary-radius shadow-primary-box-shadow"
  >
    <h3
      class="text-xl leading-[32px] tracking-[0.01rem] text-primary-text-color pb-4 font-semibold"
    >
      {{ t("phase.images") }}
    </h3>
    <!-- Image inner -->
    <div class="flex flex-col items-start gap-2 -ml-[3px]">
      <!-- Image block row -->
      <div
        v-for="input in props.step.inputs.filter((item:any)=> item.type === 'image' && item.showInMissingInputs && item.value.length === 0)"
        :key="input.id"
        @click="
          $emit(
            'setSelectedInput',
            input,
            undefined,
            input.repeatableGroupIndex
          )
        "
        class="group border py-[3px] border-transparent hover:cursor-pointer hover:bg-[#F9F8F6] hover:border-[#E3E1EA] hover:rounded-[5px] w-full flex items-center gap-1"
      >
        <h4
          class="text-base leading-5 font-normal tracking-[0.01rem] text-primary-text-color"
        >
          <span
            v-if="input.parentId !== null"
            class="text-sm font-sans font-semibold text-[#898790] leading-[17px] tracking-[0.01rem]"
          >
            <span
              v-if="step.inputs.find((i: any) => i.id === input.parentId)?.repeatableGroupDetails"
            >
              {{
                transformSlug(
                  step.inputs.find((i: any) => i.id === input.parentId)
                    ?.projectReference
                )
                  ? transformSlug(
                      step.inputs.find((i: any) => i.id === input.parentId)
                        ?.projectReference + ":"
                    )
                  : ""
              }}
              {{
                step.inputs
                  .find((i: any) => i.id === input.parentId)
                  ?.repeatableGroupDetails.find(
                    (item: any) => item.order === input.repeatableGroupIndex + 1
                  )?.name
              }}
              /
            </span>
            <span
              v-else-if="step.inputs.find((i: any) => i.id === input.parentId)?.repeatableInputNoun"
            >
              {{
                step.inputs.find((i: any) => i.id === input.parentId)
                  ?.repeatableInputNoun + ` ${input.repeatableGroupIndex + 1}`
              }}
              /
            </span>
            <span v-else
              >{{
                step.inputs.find((i: any) => i.id === input.parentId)?.name
              }}
              /</span
            >
          </span>

          {{ input.name }}
        </h4>
        <img
          class="w-3 h-3 mr-[5px] invisible group-hover:visible ml-auto"
          src="@/assets/pencil-icon.svg"
          alt="Icon"
        />
      </div>
      <!-- Repeatable inputs block -->
      <div
        v-for="input in props.step.inputs.filter((item:any)=>
          (item.type === 'repeatable_input' && item.value.some((repValue:any) => repValue.value.length === 0)))"
        :key="input.id"
        class="flex flex-col"
      >
        <div
          v-for="repetableInput in input.value.filter((val:any)=> val.value && val.value.length === 0)"
          :key="repetableInput.index"
          class="flex group border py-[3px] border-transparent hover:cursor-pointer hover:bg-[#F9F8F6] hover:border-[#E3E1EA] hover:rounded-[5px] w-full items-center gap-1"
          @click="
            $emit(
              'setSelectedInput',
              input,
              repetableInput.index,
              input.repeatableGroupIndex
            )
          "
        >
          <h4
            class="text-base leading-5 font-normal tracking-[0.01rem] text-primary-text-color"
          >
            <span
              v-if="input.parentId !== null"
              class="text-sm font-sans font-semibold text-[#898790] leading-[17px] tracking-[0.01rem]"
            >
              <span
                v-if="step.inputs.find((i: any) => i.id === input.parentId)?.repeatableGroupDetails"
              >
                {{
                  transformSlug(
                    step.inputs.find((i: any) => i.id === input.parentId)
                      ?.projectReference
                  )
                }}:
                {{
                  step.inputs
                    .find((i: any) => i.id === input.parentId)
                    ?.repeatableGroupDetails.find(
                      (item: any) => item.id === input.repeatableGroupIndex + 1
                    )?.name
                }}
                /
              </span>
              <span
                v-else-if="step.inputs.find((i: any) => i.id === input.parentId)?.repeatableInputNoun"
              >
                {{
                  step.inputs.find((i: any) => i.id === input.parentId)
                    ?.repeatableInputNoun + ` ${input.repeatableGroupIndex + 1}`
                }}
                /
              </span>
              <span v-else
                >{{
                  step.inputs.find((i: any) => i.id === input.parentId)?.name
                }}
                /</span
              >
            </span>

            <span
              v-if="input.isConditionallyRepetable"
              class="text-sm font-sans font-semibold text-[#898790] leading-[17px] tracking-[0.01rem]"
            >
              {{ input.name + ` ${repetableInput.index + 1}` }}</span
            >
            <span v-else>
              <span
                class="text-sm font-sans font-semibold text-[#898790] leading-[17px] tracking-[0.01rem]"
                >{{ input.name }} /
              </span>
              {{
                input.repeatableInputNoun + ` ${repetableInput.index + 1}`
              }}</span
            >
          </h4>
          <img
            class="w-3 h-3 mr-[5px] invisible group-hover:visible ml-auto"
            src="@/assets/pencil-icon.svg"
            alt="Icon"
          />
        </div>
      </div>
    </div>
  </div>
</template>
